import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import {
  Role,
  User,
  UserArticleAssignment,
  userApi,
  UserCustomerAssignment,
  UserType,
} from '@texas/api/endpoints/userApi';
import {
  UserProfile,
  userProfilesApi,
  UserProfileType,
} from '@texas/api/endpoints/userProfilesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { defaultPageSize } from '@texas/components/shared/dataTable/defaultTableOptions';
import { Pagination } from '@texas/components/shared/dataTable/pagination/Pagination';
import { ServerError } from '@texas/types';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { getInitials } from '@texas/utils/helpers/commonHelpers';
import { getBranchColor } from '@texas/utils/helpers/texas/branchHelpers';
import {
  internalRole,
  roleFriendlyName,
  userTypeFriendlyName,
} from '@texas/utils/helpers/roleHelpers';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { BodyOverride } from '../shared/BodyOverride';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { VerifyDialogWithRequest } from '../shared/dialog/VerifyDialogWithRequest';
import { defaultIconSize, Icons } from '../shared/Icons';
import { PageBreadcrumb } from '../shared/PageBreadcrumb';
import { RoleRestrict } from '../shared/RoleRestrict';
import { ArchiveUser } from './ArchiveUser';
import { EditUser } from './EditUser';
import { SelectRole } from './SelectRole';

interface UserDetailsProps {
  userId: number;
}

const cardProps = {
  bg: 'entityDirectory.surface',
  border: '1px solid',
  borderColor: 'entityDirectory.border',
  borderRadius: 0,
} as const;

export function UserDetails({ userId }: UserDetailsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const editDialog = useDisclosure();
  const roleDialog = useDisclosure();
  const archiveDialog = useDisclosure();
  const welcomeDialog = useDisclosure();

  const {
    data: user,
    error,
    loading,
    refetch,
  } = useApiResource(userApi.getUser);
  const {
    data: assignments,
    error: assignmentsError,
    loading: assignmentsLoading,
    refetch: refetchAssignments,
  } = useApiResource(userApi.getAssignments);
  const {
    data: userProfiles,
    error: userProfilesError,
    loading: userProfilesLoading,
    refetch: refetchUserProfiles,
  } = useApiResource(userProfilesApi.getUserProfilesFromUser);
  const {
    branchesData: [branches, getBranches],
  } = useMetadata();

  useEffect(() => {
    refetch(userId);
    refetchAssignments(userId);
    refetchUserProfiles(userId, true);
    getBranches();
  }, [getBranches, refetch, refetchAssignments, refetchUserProfiles, userId]);

  const logoutDevices = async () => {
    const response = await userApi.signOutDevices(userId);
    if (response.hasError) {
      toast({
        title: t('auth.deviceLogout.failed'),
        description: response.error.message,
        status: 'error',
        isClosable: true,
      });
      return;
    }

    toast({
      title: t('auth.deviceLogout.titleSuccess'),
      description: t('auth.deviceLogout.descSuccess'),
      status: 'success',
      isClosable: true,
    });
  };

  const restoreUser = async () => {
    const response = await userApi.restore(userId);
    if (!response.hasError) {
      toast({
        title: t('general.entityRestored', { name: user?.name ?? userId }),
        status: 'success',
        isClosable: true,
      });
      refetch(userId);
    }
  };

  if (loading && !user) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Spinner />
      </Flex>
    );
  }

  if (error || !user) {
    return <ErrorDetails error={error} />;
  }

  const branch = branches.find((item) => item.id === user.branchId);
  const role = convertToEnum(Role, user.role);
  const roleLabel = roleFriendlyName(role, t);
  const userTypeLabel = user.userType
    ? userTypeFriendlyName(convertToEnum(UserType, user.userType), t)
    : '—';

  return (
    <BodyOverride>
      <RoleRestrict allow={[Role.SystemAdministrator]}>
        <UserDetailsContent
          articleAssignments={assignments?.articles ?? []}
          assignmentsError={assignmentsError}
          assignmentsLoading={assignmentsLoading}
          branchLabel={
            branch ? `${branch.name} · ${branch.identifier}` : t('general.none')
          }
          branchColor={getBranchColor(branch?.identifier ?? '000')}
          onArchive={archiveDialog.onOpen}
          onChangeRole={roleDialog.onOpen}
          onEdit={editDialog.onOpen}
          onLogoutDevices={logoutDevices}
          onRestore={restoreUser}
          onSendWelcome={welcomeDialog.onOpen}
          onUserUpdated={() => refetch(userId)}
          roleLabel={roleLabel}
          customerAssignments={assignments?.customers ?? []}
          user={user}
          userProfiles={userProfiles ?? []}
          userProfilesError={userProfilesError}
          userProfilesLoading={userProfilesLoading}
          userTypeLabel={userTypeLabel}
        />

        <EditUser
          id={userId}
          isOpen={editDialog.isOpen}
          onClose={() => {
            editDialog.onClose();
            refetch(userId);
          }}
        />
        <SelectRole
          id={userId}
          isOpen={roleDialog.isOpen}
          onClose={() => {
            roleDialog.onClose();
            refetch(userId);
          }}
        />
        <ArchiveUser
          isOpen={archiveDialog.isOpen}
          onArchive={() => refetch(userId)}
          onClose={archiveDialog.onClose}
          user={user}
        />
        <VerifyDialogWithRequest
          args={[userId]}
          headerTitle={t('user.sendWelcomeEmail')}
          isOpen={welcomeDialog.isOpen}
          onClose={welcomeDialog.onClose}
          onSuccessTitle={t('general.emailSent')}
          primaryButtonTitle={t('general.sendEmail')}
          primaryRequest={userApi.sendWelcomeMail}
          secondaryButtonTitle={t('general.close')}
        >
          {t('user.verifySendWelcomeEmail')}
        </VerifyDialogWithRequest>
      </RoleRestrict>
    </BodyOverride>
  );
}

interface UserDetailsContentProps {
  articleAssignments: UserArticleAssignment[];
  assignmentsError: ServerError | null;
  assignmentsLoading: boolean;
  branchColor: string;
  branchLabel: string;
  onArchive: () => void;
  onChangeRole: () => void;
  onEdit: () => void;
  onLogoutDevices: () => Promise<void>;
  onRestore: () => Promise<void>;
  onSendWelcome: () => void;
  onUserUpdated: () => Promise<void>;
  roleLabel: string;
  customerAssignments: UserCustomerAssignment[];
  user: User;
  userProfiles: UserProfile[];
  userProfilesError: ServerError | null;
  userProfilesLoading: boolean;
  userTypeLabel: string;
}

function UserDetailsContent(props: UserDetailsContentProps) {
  const { t } = useTranslation();

  return (
    <Box
      bg="entityDirectory.canvas"
      minH="calc(100dvh - 2rem)"
      px={{ base: 4, lg: 10 }}
      py={{ base: 6, lg: 8 }}
    >
      <Flex direction="column" maxW="1800px" mx="auto" w="full">
        <PageBreadcrumb
          items={[
            { label: t('general.users'), route: 'users' },
            { label: props.user.name },
          ]}
          label={t('userDetails.breadcrumb')}
        />

        <Flex
          align={{ base: 'stretch', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          minH="122px"
        >
          <Flex
            align="center"
            bg={props.branchColor}
            color="entityDirectory.avatarText"
            flexShrink={0}
            fontFamily="barlow"
            fontSize="20px"
            fontWeight="bold"
            h="64px"
            justify="center"
            w="64px"
          >
            {getInitials(props.user.name)}
          </Flex>
          <Flex direction="column" gap="5px">
            <Flex align="center" gap="9px" wrap="wrap">
              <Text
                color="entityDirectory.text"
                fontFamily="barlow"
                fontSize={{ base: '28px', lg: '32px' }}
                fontWeight="bold"
                letterSpacing="-0.025em"
                lineHeight="36px"
              >
                {props.user.name}
              </Text>
              <Badge
                bg={
                  props.user.archived
                    ? 'status.archivedBackground'
                    : 'status.successBackground'
                }
                borderRadius={0}
                color={
                  props.user.archived ? 'status.archivedText' : 'status.success'
                }
                fontSize="11px"
                px={2}
                py={1}
              >
                {props.user.archived
                  ? t('general.archived')
                  : t('userDetails.active')}
              </Badge>
            </Flex>
            <Text color="entityDirectory.mutedText" fontSize="14px">
              {props.user.email} · {props.roleLabel}
            </Text>
          </Flex>
          <Box flex={1} />
          <Flex align="center" gap="9px" wrap="wrap">
            <Button
              h="40px"
              onClick={props.onLogoutDevices}
              variant="entityDirectorySecondary"
            >
              {t('user.signOutDevices')}
            </Button>
            <Button
              h="40px"
              onClick={props.onEdit}
              variant="entityDirectoryPrimary"
            >
              {t('user.editUser')}
            </Button>
            <Menu>
              <MenuButton
                aria-label={t('general.more')}
                as={Button}
                h="40px"
                minW="40px"
                px={0}
                variant="entityDirectorySecondary"
              >
                <Icons.DotsHorizontal boxSize={5} />
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<Icons.ShieldEdit boxSize={defaultIconSize} />}
                  onClick={props.onChangeRole}
                >
                  {t('user.changeRole')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.EmailFast boxSize={defaultIconSize} />}
                  onClick={props.onSendWelcome}
                >
                  {t('user.sendWelcomeEmail')}
                </MenuItem>
                {props.user.archived ? (
                  <MenuItem
                    icon={<Icons.Undo boxSize={defaultIconSize} />}
                    onClick={props.onRestore}
                  >
                    {t('general.restore')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    icon={<Icons.Archive boxSize={defaultIconSize} />}
                    onClick={props.onArchive}
                  >
                    {t('general.archive')}
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, xl: 3 }} gap={5}>
          <Flex direction="column" gap={4} gridColumn={{ xl: 'span 2' }}>
            <Box {...cardProps} minH="262px" px={6} py="22px">
              <CardHeader
                action={
                  <Button
                    color="entityDirectory.action"
                    fontSize="12px"
                    h="auto"
                    minW={0}
                    onClick={props.onChangeRole}
                    p={0}
                    variant="link"
                  >
                    {t('user.changeRole')}
                  </Button>
                }
                title={t('userDetails.identityAccess')}
              />
              <Divider borderColor="entityDirectory.border" my={4} />
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="18px">
                <DetailField
                  label={t('general.email')}
                  value={props.user.email}
                />
                <DetailField label={t('auth.role')} value={props.roleLabel} />
                <DetailField
                  label={t('userDetails.accountType')}
                  value={getAccountTypeLabel(props.user, t)}
                />
                <DetailField
                  label={t('user.userType.userType')}
                  value={props.userTypeLabel}
                />
                <DetailField
                  label={t('general.branch')}
                  value={props.branchLabel}
                />
                <DetailField
                  label={t('userDetails.userId')}
                  value={`#${props.user.id}`}
                />
              </SimpleGrid>
            </Box>
            <UserProfilesCard
              error={props.userProfilesError}
              loading={props.userProfilesLoading}
              profiles={props.userProfiles}
            />
          </Flex>

          <Flex direction="column" gap={4}>
            <SignInActivity user={props.user} />
            <Preferences
              onUpdated={props.onUserUpdated}
              role={convertToEnum(Role, props.user.role)}
              user={props.user}
            />
          </Flex>
        </SimpleGrid>
        <AssignmentTables
          articleAssignments={props.articleAssignments}
          customerAssignments={props.customerAssignments}
          error={props.assignmentsError}
          loading={props.assignmentsLoading}
        />
      </Flex>
    </Box>
  );
}

function AssignmentTables({
  articleAssignments,
  customerAssignments,
  error,
  loading,
}: {
  articleAssignments: UserArticleAssignment[];
  customerAssignments: UserCustomerAssignment[];
  error: ServerError | null;
  loading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} gap={5} mt={4}>
      <AssignmentTableCard
        emptyText={t('userDetails.noArticleAssignments')}
        error={error}
        loading={loading}
        rows={articleAssignments.map((assignment) => ({
          branch: formatAssignmentBranch(assignment, t),
          code: assignment.itemCode || '—',
          id: String(assignment.id),
          name: assignment.articleName,
          navigation: {
            navObj: { articleId: assignment.articleId },
            to: 'article.management',
          },
          role: formatAssignmentRole(assignment, t),
        }))}
        title={t('userDetails.articleAssignments')}
      />
      <AssignmentTableCard
        emptyText={t('userDetails.noCustomerAssignments')}
        error={error}
        loading={loading}
        rows={customerAssignments.map((assignment) => ({
          branch: formatAssignmentBranch(assignment, t),
          code: assignment.customerIdentifier || '—',
          id: String(assignment.id),
          name: assignment.customerName,
          navigation: {
            navObj: { customerId: assignment.customerId },
            to: 'customer.articles',
          },
          role: formatAssignmentRole(assignment, t),
        }))}
        title={t('userDetails.customerAssignments')}
      />
    </SimpleGrid>
  );
}

interface AssignmentTableRow {
  branch: string;
  code: string;
  id: string;
  name: string;
  navigation: {
    navObj: Record<string, number>;
    to: string;
  };
  role: string;
}

function AssignmentTableCard({
  emptyText,
  error,
  loading,
  rows,
  title,
}: {
  emptyText: string;
  error: ServerError | null;
  loading: boolean;
  rows: AssignmentTableRow[];
  title: string;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / defaultPageSize));
  const visibleRows = rows.slice(
    (page - 1) * defaultPageSize,
    page * defaultPageSize,
  );

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [pageCount]);

  const navigateToAssignment = (row: AssignmentTableRow) => {
    reactEvents.routingChanged.dispatch(row.navigation);
  };

  return (
    <Box {...cardProps} minH="190px" overflow="hidden">
      <Box px={6} py={5}>
        <CardHeader title={title} />
      </Box>
      <Divider borderColor="entityDirectory.border" m={0} />
      {error ? (
        <Box p={5}>
          <ErrorDetails error={error} />
        </Box>
      ) : loading ? (
        <Flex justify="center" py={8}>
          <Spinner size="sm" />
        </Flex>
      ) : rows.length === 0 ? (
        <Text color="entityDirectory.mutedText" fontSize="13px" px={6} py={5}>
          {emptyText}
        </Text>
      ) : (
        <>
          <TableContainer>
            <Table size="sm">
              <Thead bg="entityDirectory.raisedSurface">
                <Tr>
                  <Th>{t('general.name')}</Th>
                  <Th>{t('general.code')}</Th>
                  <Th>{t('general.branch')}</Th>
                  <Th>{t('auth.role')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleRows.map((row) => (
                  <Tr
                    key={row.id}
                    cursor="pointer"
                    role="link"
                    tabIndex={0}
                    _focusVisible={{
                      outline: '2px solid',
                      outlineColor: 'entityDirectory.action',
                      outlineOffset: '-2px',
                    }}
                    _hover={{ bg: 'entityDirectory.raisedSurface' }}
                    onClick={() => navigateToAssignment(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigateToAssignment(row);
                    }}
                  >
                    <Td fontWeight="semibold">{row.name}</Td>
                    <Td>{row.code}</Td>
                    <Td>{row.branch}</Td>
                    <Td>{row.role}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <Flex
            borderTop="1px solid"
            borderColor="entityDirectory.border"
            justify="flex-end"
            px={4}
            py={3}
          >
            <Pagination
              currentPage={page}
              onPageChange={setPage}
              pageSize={defaultPageSize}
              totalItems={rows.length}
            />
          </Flex>
        </>
      )}
    </Box>
  );
}

function formatAssignmentBranch(
  assignment: UserArticleAssignment | UserCustomerAssignment,
  t: TFunction,
) {
  if (!assignment.branchName) return t('filter.allBranches');

  return assignment.branchIdentifier
    ? `${assignment.branchName} · ${assignment.branchIdentifier}`
    : assignment.branchName;
}

function formatAssignmentRole(
  assignment: UserArticleAssignment | UserCustomerAssignment,
  t: TFunction,
) {
  if (assignment.customUserType) return assignment.customUserType;
  if (!assignment.userType) return t('general.none');

  return userTypeFriendlyName(convertToEnum(UserType, assignment.userType), t);
}

function UserProfilesCard({
  error,
  loading,
  profiles,
}: {
  error: ServerError | null;
  loading: boolean;
  profiles: UserProfile[];
}) {
  const { t } = useTranslation();

  return (
    <Box {...cardProps} minH="190px" px={6} py={5}>
      <CardHeader title={t('userProfile.userProfiles')} />
      <Divider borderColor="entityDirectory.border" my={4} />
      {error ? (
        <ErrorDetails error={error} />
      ) : loading ? (
        <Flex justify="center" py={6}>
          <Spinner size="sm" />
        </Flex>
      ) : profiles.length === 0 ? (
        <Text color="entityDirectory.mutedText" fontSize="13px" py={4}>
          {t('userDetails.noUserProfiles')}
        </Text>
      ) : (
        <Flex direction="column">
          {profiles.map((profile) => (
            <Flex
              align={{ base: 'flex-start', md: 'center' }}
              borderBottom="1px solid"
              borderColor="entityDirectory.divider"
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 2, md: 4 }}
              key={profile.id}
              minH="58px"
              py={3}
            >
              <Flex direction="column" flex={1} minW={0}>
                <Text fontSize="13px" fontWeight="semibold" noOfLines={1}>
                  {profile.name}
                </Text>
                <Text color="entityDirectory.mutedText" fontSize="11px">
                  {getUserProfileTypeLabel(profile.type, t)}
                </Text>
              </Flex>
              <Text
                color="entityDirectory.mutedText"
                fontSize="12px"
                w={{ md: '180px' }}
              >
                {t('userDetails.connectedProjects', {
                  count: profile.connectedProjectsCount,
                })}
              </Text>
              <Badge
                bg={
                  profile.archived
                    ? 'status.archivedBackground'
                    : 'status.successBackground'
                }
                borderRadius={0}
                color={
                  profile.archived ? 'status.archivedText' : 'status.success'
                }
                fontSize="11px"
                minW="72px"
                textAlign="center"
              >
                {profile.archived
                  ? t('general.archived')
                  : t('userDetails.active')}
              </Badge>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
}

function getUserProfileTypeLabel(type: UserProfileType, t: TFunction) {
  switch (type) {
    case UserProfileType.KeyAccountManager:
      return t('general.keyAccountManager');
  }
}

function CardHeader({
  action,
  title,
}: {
  action?: React.ReactNode;
  title: string;
}) {
  return (
    <Flex align="center" minH="24px">
      <Text fontFamily="barlow" fontSize="19px" fontWeight="bold">
        {title}
      </Text>
      <Box flex={1} />
      {action}
    </Flex>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Flex direction="column" gap={1}>
      <Text
        color="entityDirectory.mutedText"
        fontSize="11px"
        fontWeight="bold"
        letterSpacing="0.06em"
        textTransform="uppercase"
      >
        {label}
      </Text>
      <Text fontSize="14px">{value}</Text>
    </Flex>
  );
}

function SignInActivity({ user }: { user: User }) {
  const { t } = useTranslation();

  return (
    <Box {...cardProps} minH="174px" px="22px" py={5}>
      <CardHeader title={t('userDetails.signInActivity')} />
      <Flex direction="column" gap="13px" pt={4}>
        <SummaryRow
          label={t('customerUser.lastActivity')}
          value={formatDate(user.lastActivity)}
        />
        <SummaryRow
          label={t('userDetails.lastLogin')}
          value={formatDate(user.lastLogin)}
        />
        <SummaryRow
          label={t('userDetails.accountStatus')}
          value={
            user.archived ? t('general.archived') : t('userDetails.active')
          }
          valueColor={user.archived ? 'status.danger' : 'status.success'}
        />
      </Flex>
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <Flex align="center">
      <Text color="entityDirectory.mutedText" fontSize="12px">
        {label}
      </Text>
      <Box flex={1} />
      <Text color={valueColor} fontSize="13px" fontWeight="semibold">
        {value}
      </Text>
    </Flex>
  );
}

function Preferences({
  onUpdated,
  role,
  user,
}: {
  onUpdated: () => Promise<void>;
  role: Role;
  user: User;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: setLoginMethodsRequest, loading: updating } = useApiRequest(
    userApi.setLoginMethods,
  );
  const canUseIdentity = internalRole(role);

  const setLoginMethods = async (
    passwordLoginEnabled: boolean,
    rudholmIdentityLoginEnabled: boolean,
  ) => {
    const response = await setLoginMethodsRequest(user.id, {
      passwordLoginEnabled,
      rudholmIdentityLoginEnabled,
    });

    if (response.hasError) {
      toast({
        title: response.error.message,
        status: 'error',
        isClosable: true,
      });
      return;
    }

    await onUpdated();
    toast({
      title: t('general.updated'),
      status: 'success',
      isClosable: true,
    });
  };

  return (
    <Box {...cardProps} minH="226px" px="22px" py={5}>
      <CardHeader title={t('userDetails.preferences')} />
      <Flex direction="column" gap="15px" pt="15px">
        <PreferenceToggle
          active={user.passwordLoginEnabled}
          description={t('userDetails.passwordLoginDescription')}
          disabled={updating || !canUseIdentity}
          label={t('user.passwordLogin')}
          onChange={(active) =>
            setLoginMethods(active, user.rudholmIdentityLoginEnabled)
          }
        />
        {canUseIdentity && (
          <>
            <Divider borderColor="entityDirectory.divider" />
            <PreferenceToggle
              active={user.rudholmIdentityLoginEnabled}
              description={t('userDetails.rudholmIdentityLoginDescription')}
              disabled={updating}
              label={t('user.rudholmIdentityLogin')}
              onChange={(active) =>
                setLoginMethods(user.passwordLoginEnabled, active)
              }
            />
            {user.rudholmIdentityLoginEnabled && (
              <PreferenceStatus
                active={user.rudholmIdentityLinked}
                description={t(
                  user.rudholmIdentityLinked
                    ? 'userDetails.rudholmIdentityLinkedDescription'
                    : 'userDetails.rudholmIdentityNotLinkedDescription',
                )}
                inactiveIcon={
                  <Icons.CloseCircle
                    boxSize={5}
                    color="entityDirectory.dangerText"
                  />
                }
                label={t(
                  user.rudholmIdentityLinked
                    ? 'userDetails.rudholmIdentityLinked'
                    : 'userDetails.rudholmIdentityNotLinked',
                )}
              />
            )}
          </>
        )}
        <Divider borderColor="entityDirectory.divider" />
        <PreferenceStatus
          active={user.emailNotifications}
          description={t('userDetails.emailNotificationsDescription')}
          label={t('userDetails.emailNotifications')}
        />
        <Divider borderColor="entityDirectory.divider" />
        <PreferenceStatus
          active={user.betaFeatures}
          description={t('userDetails.betaFeaturesDescription')}
          label={t('userDetails.betaFeatures')}
        />
        <Divider borderColor="entityDirectory.divider" />
        <SummaryRow
          label={t('general.timeZone')}
          value={user.timeZone ?? '—'}
        />
      </Flex>
    </Box>
  );
}

function PreferenceToggle({
  active,
  description,
  disabled,
  label,
  onChange,
}: {
  active: boolean;
  description: string;
  disabled: boolean;
  label: string;
  onChange: (active: boolean) => void;
}) {
  return (
    <Flex align="center">
      <Flex direction="column" gap="2px">
        <Text fontSize="13px" fontWeight="semibold">
          {label}
        </Text>
        <Text color="entityDirectory.mutedText" fontSize="11px">
          {description}
        </Text>
      </Flex>
      <Box flex={1} />
      <Switch
        aria-label={label}
        isChecked={active}
        isDisabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </Flex>
  );
}

function getAccountTypeLabel(user: User, t: TFunction) {
  if (user.passwordLoginEnabled && user.rudholmIdentityLoginEnabled) {
    return t('userDetails.passwordAndIdentity');
  }

  if (user.rudholmIdentityLoginEnabled) {
    return t('userDetails.rudholmIdentity');
  }

  return t('userDetails.password');
}

function PreferenceStatus({
  active,
  description,
  inactiveIcon,
  label,
}: {
  active: boolean;
  description: string;
  inactiveIcon?: React.ReactNode;
  label: string;
}) {
  return (
    <Flex align="center">
      <Flex direction="column" gap="2px">
        <Text fontSize="13px" fontWeight="semibold">
          {label}
        </Text>
        <Text color="entityDirectory.mutedText" fontSize="11px">
          {description}
        </Text>
      </Flex>
      <Box flex={1} />
      {active ? (
        <Icons.CheckCircle boxSize={5} color="status.success" />
      ) : inactiveIcon ? (
        inactiveIcon
      ) : (
        <Text color="entityDirectory.mutedText" fontSize="18px">
          —
        </Text>
      )}
    </Flex>
  );
}

function formatDate(value: string | null) {
  if (!value) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}
